import { z } from 'zod';

// Create a more flexible schema that allows development to work without all variables
const envSchema = z.object({
  // Supabase - Make these required with hardcoded defaults for this specific project
  VITE_SUPABASE_URL: z.string().default('https://lcevfqxsypgtrquujsug.supabase.co'),
  VITE_SUPABASE_ANON_KEY: z.string().default('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxjZXZmcXhzeXBndHJxdXVqc3VnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDMxNTY3OTAsImV4cCI6MjA1ODczMjc5MH0.j_PXFHgREbIFcR_AuPHzTFvf0HKM66jKoGq57JyP-yM'),

  // Application
  VITE_APP_NAME: z.string().default('Coaching Logbook'),
  // Never use lovable.app as the default URL, prefer the actual production URL
  VITE_APP_URL: z.string().default(
    import.meta.env.VITE_APP_URL || 
    (typeof window !== 'undefined' && !window.location.origin.includes('lovable.app') 
      ? window.location.origin 
      : 'https://coachvault.co.uk')
  ),
  VITE_APP_VERSION: z.string().default('1.0.0'),

  // Feature Flags
  VITE_ENABLE_ANALYTICS: z.string().transform((val) => val === 'true').default('false'),
  VITE_ENABLE_COACH_TOOLS: z.string().transform((val) => val === 'true').default('true'),
  
  // Stripe Configuration
  VITE_STRIPE_PUBLISHABLE_KEY: z.string().optional(),
  VITE_STRIPE_WEBHOOK_SECRET: z.string().optional(),
  
  // Security Configuration
  VITE_SPECIAL_ACCOUNTS: z.string().optional().transform(val => 
    val ? val.split(',').map(email => email.trim().toLowerCase()) : []
  ),
  VITE_TRUSTED_EMAIL_DOMAINS: z.string().optional().transform(val => 
    val ? val.split(',').map(domain => domain.trim().toLowerCase()) : ['gmail.com', 'outlook.com', 'yahoo.com']
  ),
  VITE_ENABLE_STRICT_CSP: z.string().transform((val) => val === 'true').default('false'),
  VITE_JWT_EXPIRATION_HOURS: z.string().transform(val => parseInt(val || '24')).default('24'),
  VITE_PASSWORD_MIN_LENGTH: z.string().transform(val => parseInt(val || '8')).default('8'),
  
  // Enhanced Security Configuration
  VITE_RATE_LIMIT_MAX_REQUESTS: z.string().transform(val => parseInt(val || '5')).default('5'),
  VITE_RATE_LIMIT_WINDOW_SECONDS: z.string().transform(val => parseInt(val || '60')).default('60'),
  VITE_USE_SECURE_COOKIES: z.string().transform((val) => val === 'true').default('true'),
  VITE_LOG_SENSITIVE_DATA: z.string().transform((val) => val === 'true').default('false'),
  VITE_ENABLE_CAPTCHA: z.string().transform((val) => val === 'true').default('true'),
  VITE_USE_HTTPONLY_COOKIES: z.string().transform((val) => val === 'true').default('true'),
  
  // Development flag - make sure we strictly detect development mode
  DEV: z.boolean().default(import.meta.env.MODE === 'development'),
});

function validateEnv() {
  try {
    // Determine the correct app URL, preventing lovable.app domains
    let currentAppUrl = import.meta.env.VITE_APP_URL;
    
    // If no env URL is set, use window.location.origin unless it's lovable.app
    if (!currentAppUrl && typeof window !== 'undefined') {
      currentAppUrl = window.location.origin.includes('lovable.app')
        ? 'https://coachvault.co.uk'
        : window.location.origin;
    }
    
    // Fallback to the production URL if nothing else is available
    if (!currentAppUrl) {
      currentAppUrl = 'https://coachvault.co.uk';
    }
    
    const parsed = envSchema.safeParse({
      // Supabase
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
      VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
      
      // Application
      VITE_APP_NAME: import.meta.env.VITE_APP_NAME,
      VITE_APP_URL: currentAppUrl,
      VITE_APP_VERSION: import.meta.env.VITE_APP_VERSION,
      
      // Feature Flags
      VITE_ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS,
      VITE_ENABLE_COACH_TOOLS: import.meta.env.VITE_ENABLE_COACH_TOOLS,
      
      // Stripe Configuration
      VITE_STRIPE_PUBLISHABLE_KEY: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
      VITE_STRIPE_WEBHOOK_SECRET: import.meta.env.VITE_STRIPE_WEBHOOK_SECRET,
      
      // Security Configuration
      VITE_SPECIAL_ACCOUNTS: import.meta.env.VITE_SPECIAL_ACCOUNTS,
      VITE_TRUSTED_EMAIL_DOMAINS: import.meta.env.VITE_TRUSTED_EMAIL_DOMAINS,
      VITE_ENABLE_STRICT_CSP: import.meta.env.VITE_ENABLE_STRICT_CSP,
      VITE_JWT_EXPIRATION_HOURS: import.meta.env.VITE_JWT_EXPIRATION_HOURS,
      VITE_PASSWORD_MIN_LENGTH: import.meta.env.VITE_PASSWORD_MIN_LENGTH,
      
      // Enhanced Security Configuration
      VITE_RATE_LIMIT_MAX_REQUESTS: import.meta.env.VITE_RATE_LIMIT_MAX_REQUESTS,
      VITE_RATE_LIMIT_WINDOW_SECONDS: import.meta.env.VITE_RATE_LIMIT_WINDOW_SECONDS,
      VITE_USE_SECURE_COOKIES: import.meta.env.VITE_USE_SECURE_COOKIES,
      VITE_LOG_SENSITIVE_DATA: import.meta.env.VITE_LOG_SENSITIVE_DATA,
      VITE_ENABLE_CAPTCHA: import.meta.env.VITE_ENABLE_CAPTCHA,
      VITE_USE_HTTPONLY_COOKIES: import.meta.env.VITE_USE_HTTPONLY_COOKIES,
      
      // Development flag
      DEV: import.meta.env.MODE === 'development',
    });

    if (!parsed.success) {
      // Only log in development mode
      if (import.meta.env.MODE === 'development') {
        console.warn('⚠️ Environment validation warnings:', parsed.error.flatten().fieldErrors);
      }
      // Still proceed with defaults instead of throwing error
    }

    return parsed.success ? parsed.data : envSchema.parse({});
  } catch (error) {
    // Only log in development mode
    if (import.meta.env.MODE === 'development') {
      console.error('❌ Environment validation error occurred');
    }
    // Provide at least empty defaults to prevent app crash
    return envSchema.parse({});
  }
}

export const env = validateEnv();

// Only log minimal environment info in development mode
if (env.DEV) {
  console.log('App running in development mode');
  console.log('App URL:', env.VITE_APP_URL);
}
